import type { GlobalAction } from '../types/GlobalAction.types';

/**
 * The in-flight rule of the Global Action editor (Mike, 2026-09-26). An enabled IPAWS Global Action
 * with a message on the air cannot be edited. The operator disables it, edits it, and enables it
 * again. The backend refuses the save with 409 `GLOBAL_ACTION_IN_FLIGHT` for the same rule.
 */

/** The code of the backend 409 answer (`PUT /v2/global-action/:id`). */
export const GLOBAL_ACTION_IN_FLIGHT_CODE = 'GLOBAL_ACTION_IN_FLIGHT';

/** The one sentence that the editor shows for an action in flight and for a 409 refusal. */
export const IPAWS_IN_FLIGHT_NOTICE =
  'This Global Action has an IPAWS message on the air. Disable it to edit it.';

/** The two fields of the loaded action (the `GET /v2/global-action` row) that decide the rule. */
export type IpawsInFlightSource = Partial<Pick<GlobalAction, 'ipaws_identifier'>> & {
  /** The DB `status` column: a tinyint (`0 | 1`) or a boolean, as the backend contract allows. */
  status?: number | boolean;
};

/**
 * Tell if the loaded action is enabled and has an IPAWS message on the air. A disabled action is
 * never in flight. An empty identifier counts as no identifier, as in the backend.
 */
export const isIpawsActionInFlight = (action: IpawsInFlightSource | null | undefined): boolean => {
  if (!action) return false;
  const enabled = action.status === true || action.status === 1;
  const onTheAir = typeof action.ipaws_identifier === 'string' && action.ipaws_identifier !== '';
  return enabled && onTheAir;
};

/** The body that the backend sends with the 409 answer. */
type InFlightRefusalBody = { code?: string; message?: string } | null | undefined;

/** The RTK mutation error: axiosBaseQuery puts the body under `raw`; other paths use `data`. */
export type GlobalActionSaveErrorShape =
  | { status?: number | string; raw?: InFlightRefusalBody; data?: InFlightRefusalBody }
  | null
  | undefined;

/** Tell if a failed save is the backend 409 in-flight refusal (the action triggered while open). */
export const isIpawsInFlightRefusal = (error: GlobalActionSaveErrorShape): boolean => {
  if (!error || error.status !== 409) return false;
  const code = error.raw?.code ?? error.data?.code;
  return code === GLOBAL_ACTION_IN_FLIGHT_CODE;
};
